import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, doc, updateDoc, deleteDoc, collection, query, where, getDocs, onSnapshot
} from '../../firebase/config';
import {
  User, Mail, Phone, Calendar, Edit2, Save,
  Heart, ShoppingBag,
  LogOut, Briefcase, ArrowRight, Bell,
  Lock, Globe, Shield, CreditCard, ChevronRight, MessageCircle, Clock,
  Package, MapPin, X, Check, Trash2, AlertTriangle
} from 'lucide-react';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, userData, setUserData, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [userSettings, setUserSettings] = useState({
    notifications: true,
    language: 'English (US)',
  });

  // Shipping address state
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    fullName: '', phone: '', line1: '', city: '', postalCode: '', country: 'South Africa'
  });
  const [addressToast, setAddressToast] = useState(null);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  // ---------- Load user data + related collections ----------
  useEffect(() => {
    if (userData) {
      setFormData({
        name: userData.name || '',
        phone: userData.phone || '',
        email: userData.email || '',
      });

      setUserSettings({
        notifications: userData.notifications !== false,
        language: userData.language || userData.locale || 'English (US)',
      });

      if (userData.shippingAddress) {
        setAddressForm({
          fullName: userData.shippingAddress.fullName || userData.name || '',
          phone: userData.shippingAddress.phone || userData.phone || '',
          line1: userData.shippingAddress.line1 || userData.shippingAddress.address || '',
          city: userData.shippingAddress.city || '',
          postalCode: userData.shippingAddress.postalCode || '',
          country: userData.shippingAddress.country || 'South Africa',
        });
      } else {
        setAddressForm(prev => ({
          ...prev,
          fullName: userData.name || '',
          phone: userData.phone || '',
        }));
      }
    }

    if (!user?.uid) return;

    const ordersQuery = query(
      collection(db, 'orders'),
      where('clientId', '==', user.uid)
    );
    const unsubOrders = onSnapshot(ordersQuery, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => {
        const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
        const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
        return bT - aT;
      });
      setOrders(data);
    }, (err) => console.error('Orders snapshot:', err));

    const favQuery = query(
      collection(db, 'users', user.uid, 'favorites')
    );
    const unsubFavs = onSnapshot(favQuery, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFavorites(data);
    }, (err) => console.error('Favorites snapshot:', err));

    const apptQuery = query(
      collection(db, 'bookings'),
      where('clientId', '==', user.uid)
    );
    const unsubAppts = onSnapshot(apptQuery, (snapshot) => {
      const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const upcoming = data.filter(b => {
        const dt = new Date(`${b.date}T${b.time || '00:00'}`).getTime();
        return dt >= Date.now() && b.status !== 'cancelled' && b.status !== 'completed';
      });
      upcoming.sort((a, b) => {
        const aT = new Date(`${a.date}T${a.time || '00:00'}`).getTime();
        const bT = new Date(`${b.date}T${b.time || '00:00'}`).getTime();
        return aT - bT;
      });
      setAppointments(upcoming);
    }, (err) => console.error('Appointments snapshot:', err));

    return () => {
      unsubOrders();
      unsubFavs();
      unsubAppts();
    };
  }, [user?.uid, userData]);

  // ---------- Toast ----------
  const showAddressToast = (msg, type = 'success') => {
    setAddressToast({ msg, type });
    setTimeout(() => setAddressToast(null), 2200);
  };

  // ---------- Member since helper ----------
  const getMemberSince = () => {
    const raw = userData?.createdAt;
    if (!raw) return '—';

    let ts;
    if (raw?.toMillis) ts = raw.toMillis();
    else if (typeof raw === 'string' || typeof raw === 'number') {
      ts = new Date(raw).getTime();
    } else {
      return '—';
    }

    if (!ts || Number.isNaN(ts)) return '—';
    return new Date(ts).getFullYear().toString();
  };

  // ---------- Save profile ----------
  const handleSave = async () => {
    setLoading(true);
    try {
      const updates = {
        name: formData.name,
        phone: formData.phone,
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'users', user.uid), updates);
      if (setUserData) setUserData(prev => ({ ...prev, ...updates }));
      setIsEditing(false);
      showAddressToast('Profile updated');
    } catch (error) {
      console.error('Error updating profile:', error);
      showAddressToast('Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFormData({
      name: userData?.name || '',
      phone: userData?.phone || '',
      email: userData?.email || '',
    });
  };

  // ---------- Save address ----------
  const handleSaveAddress = async () => {
    if (!addressForm.fullName.trim() || !addressForm.line1.trim() ||
        !addressForm.city.trim() || !addressForm.postalCode.trim()) {
      showAddressToast('Please fill all required fields', 'error');
      return;
    }

    setSavingAddress(true);
    try {
      const shippingAddress = {
        fullName: addressForm.fullName.trim(),
        phone: addressForm.phone.trim(),
        line1: addressForm.line1.trim(),
        city: addressForm.city.trim(),
        postalCode: addressForm.postalCode.trim(),
        country: addressForm.country || 'South Africa',
        updatedAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, 'users', user.uid), {
        shippingAddress,
        updatedAt: new Date().toISOString(),
      });

      if (setUserData) setUserData(prev => ({ ...prev, shippingAddress }));
      setShowAddressModal(false);
      showAddressToast('Address saved');
    } catch (err) {
      console.error('Save address failed:', err);
      showAddressToast('Failed to save address', 'error');
    } finally {
      setSavingAddress(false);
    }
  };

  // ---------- Logout → home page ----------
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSwitchToPro = async () => {
    try {
      await logout();
      navigate('/register', { state: { role: 'professional', fromSwitch: true } });
    } catch (error) {
      console.error('Switch to Pro error:', error);
    }
  };

  // ---------- Delete account ----------
  const handleDeleteAccount = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== 'DELETE') {
      showAddressToast('Type DELETE to confirm', 'error');
      return;
    }

    setDeletingAccount(true);
    try {
      // Remove cart if present
      try {
        await deleteDoc(doc(db, 'carts', user.uid));
      } catch (err) {
        // cart may not exist — safe to ignore
      }

      // Remove user's favorites subcollection
      try {
        const favSnap = await getDocs(collection(db, 'users', user.uid, 'favorites'));
        await Promise.all(favSnap.docs.map(d => deleteDoc(doc(db, 'users', user.uid, 'favorites', d.id))));
      } catch (err) {
        console.warn('Failed to clear favorites:', err);
      }

      // Remove user's maintenance subcollection
      try {
        const maintSnap = await getDocs(collection(db, 'users', user.uid, 'maintenance'));
        await Promise.all(maintSnap.docs.map(d => deleteDoc(doc(db, 'users', user.uid, 'maintenance', d.id))));
      } catch (err) {
        console.warn('Failed to clear maintenance:', err);
      }

      // Delete the user profile document
      await deleteDoc(doc(db, 'users', user.uid));

      // Sign out — onAuthStateChanged will clear context
      await logout();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Delete account error:', err);
      showAddressToast('Failed to delete account. Please try again.', 'error');
      setDeletingAccount(false);
    }
  };

  const updateSetting = async (key, value) => {
    if (!user) return;
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        [key]: value,
        updatedAt: new Date().toISOString()
      });
      setUserSettings(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      console.error('Error updating setting:', error);
    }
  };

  const getAddressPreview = () => {
    const addr = userData?.shippingAddress;
    if (!addr) return 'Not set';
    const parts = [addr.line1, addr.city].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not set';
  };

  // ---------- Settings sections ----------
  const settingsSections = [
    {
      title: 'Shopping',
      items: [
        {
          icon: <Package size={18} />,
          label: 'My Orders',
          description: orders.length > 0
            ? `${orders.length} order${orders.length === 1 ? '' : 's'}`
            : 'No orders yet',
          onClick: () => navigate('/dashboard/orders'),
        },
        {
          icon: <MapPin size={18} />,
          label: 'Shipping Address',
          description: getAddressPreview(),
          onClick: () => setShowAddressModal(true),
        },
        {
          icon: <Heart size={18} />,
          label: 'Favorites',
          description: favorites.length > 0
            ? `${favorites.length} saved item${favorites.length === 1 ? '' : 's'}`
            : 'No favorites yet',
          onClick: () => navigate('/dashboard/favorites'),
        },
      ]
    },
    {
      title: 'Preferences',
      items: [
        {
          icon: <Bell size={18} />,
          label: 'Notifications',
          description: userSettings.notifications ? 'Enabled' : 'Disabled',
          toggle: true,
          key: 'notifications',
          value: userSettings.notifications
        },
        {
          icon: <Globe size={18} />,
          label: 'Language',
          description: userSettings.language,
          key: 'language',
          onClick: () => navigate('/dashboard/settings/language'),
        },
      ]
    },
    {
      title: 'Account',
      items: [
        {
          icon: <Lock size={18} />,
          label: 'Privacy & Security',
          description: 'Manage your privacy settings',
          key: 'privacy',
          onClick: () => navigate('/dashboard/settings/privacy'),
        },
        {
          icon: <CreditCard size={18} />,
          label: 'Payment Methods',
          description: 'Manage your payment options',
          key: 'payment',
          onClick: () => navigate('/dashboard/settings/payment-methods'),
        },
        {
          icon: <Shield size={18} />,
          label: 'Data & Permissions',
          description: 'Control your data',
          key: 'data',
          onClick: () => navigate('/dashboard/settings/data'),
        },
        {
          icon: <Trash2 size={18} />,
          label: 'Delete Account',
          description: 'Permanently delete your account',
          key: 'delete',
          onClick: () => { setShowDeleteModal(true); setDeleteConfirmText(''); },
          danger: true,
        },
      ]
    },
    {
      title: 'Support',
      items: [
        {
          icon: <MessageCircle size={18} />,
          label: 'Help Center',
          description: 'Get help and support',
          key: 'help',
          onClick: () => navigate('/dashboard/settings/help'),
        },
        {
          icon: <Lock size={18} />,
          label: 'Privacy Policy',
          description: 'Read our privacy policy',
          key: 'privacy-policy',
          onClick: () => navigate('/dashboard/settings/privacy-policy'),
        },
        {
          icon: <Lock size={18} />,
          label: 'Terms of Service',
          description: 'Read our terms',
          key: 'terms',
          onClick: () => navigate('/dashboard/settings/terms'),
        },
      ]
    }
  ];

  // ============================================================
  // SETTINGS LIST
  // ============================================================
  const SettingsList = ({ compact = false }) => (
    <div className={compact ? 'space-y-4' : 'space-y-6'}>
      {settingsSections.map((section, index) => (
        <div key={index} className="bg-white rounded-3xl shadow-sm border border-gray-50 overflow-hidden">
          <div className={`${compact ? 'px-4 pt-4 pb-2' : 'p-4 border-b border-gray-50'}`}>
            <h3 className={`font-bold text-gray-400 uppercase tracking-wider ${
              compact ? 'text-[10px]' : 'text-sm'
            }`}>
              {section.title}
            </h3>
          </div>
          <div>
            {section.items.map((item, itemIndex) => {
              const clickable = !item.toggle && item.onClick;
              const isDanger = !!item.danger;
              return (
                <div
                  key={itemIndex}
                  onClick={clickable ? item.onClick : undefined}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    clickable
                      ? isDanger
                        ? 'cursor-pointer active:bg-red-50 hover:bg-red-50'
                        : 'cursor-pointer active:bg-gray-50 hover:bg-gray-50'
                      : 'hover:bg-gray-50'
                  } ${itemIndex < section.items.length - 1 ? 'border-b border-gray-50' : ''}`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                        isDanger
                          ? 'bg-red-50 text-red-400'
                          : 'bg-[#FCF8F9] text-gray-600'
                      }`}
                    >
                      {item.icon}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`font-medium text-sm ${isDanger ? 'text-red-500' : 'text-gray-900'}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{item.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {item.toggle ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          item.onToggle ? item.onToggle() : updateSetting(item.key, !item.value);
                        }}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          item.value ? 'bg-[#EFA9B6]' : 'bg-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                          item.value ? 'translate-x-6' : 'translate-x-0.5'
                        } mt-0.5`} />
                      </button>
                    ) : (
                      <ChevronRight
                        size={18}
                        className={isDanger ? 'text-red-300' : 'text-gray-400'}
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  // ============================================================
  // ADDRESS MODAL
  // ============================================================
  const AddressModal = () => (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 border-b border-gray-50 flex items-center justify-between shrink-0">
          <div>
            <h2 className="font-serif font-bold text-xl text-gray-900">Shipping Address</h2>
            <p className="text-xs text-gray-400 mt-0.5">Used to prefill checkout</p>
          </div>
          <button
            onClick={() => setShowAddressModal(false)}
            className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <Field
            label="Full Name *"
            value={addressForm.fullName}
            onChange={(v) => setAddressForm(a => ({ ...a, fullName: v }))}
            placeholder="John Doe"
          />
          <Field
            label="Phone"
            type="tel"
            value={addressForm.phone}
            onChange={(v) => setAddressForm(a => ({ ...a, phone: v }))}
            placeholder="+27 82 123 4567"
          />
          <Field
            label="Street Address *"
            value={addressForm.line1}
            onChange={(v) => setAddressForm(a => ({ ...a, line1: v }))}
            placeholder="123 Main Street"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="City *"
              value={addressForm.city}
              onChange={(v) => setAddressForm(a => ({ ...a, city: v }))}
              placeholder="Cape Town"
            />
            <Field
              label="Postal Code *"
              value={addressForm.postalCode}
              onChange={(v) => setAddressForm(a => ({ ...a, postalCode: v }))}
              placeholder="8001"
            />
          </div>
          <Field
            label="Country"
            value={addressForm.country}
            onChange={(v) => setAddressForm(a => ({ ...a, country: v }))}
            placeholder="South Africa"
          />
        </div>

        <div className="p-5 pt-4 border-t border-gray-50 shrink-0">
          <button
            onClick={handleSaveAddress}
            disabled={savingAddress}
            className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {savingAddress ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              <><Check size={16} /> Save Address</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // DELETE ACCOUNT MODAL
  // ============================================================
  const DeleteAccountModal = () => (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={26} className="text-red-500" />
          </div>
          <h2 className="font-serif font-bold text-xl text-gray-900 text-center mb-2">
            Delete your account?
          </h2>
          <p className="text-sm text-gray-500 text-center mb-6">
            This will permanently remove your profile, favorites, and maintenance routines.
            Bookings and orders you've already placed will remain in your history.
            This cannot be undone.
          </p>

          <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
            Type <span className="text-red-500">DELETE</span> to confirm
          </label>
          <input
            type="text"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="DELETE"
            className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-red-400 focus:bg-white transition-colors"
          />

          <div className="flex gap-3 mt-5">
            <button
              onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
              disabled={deletingAccount}
              className="flex-1 py-3.5 rounded-full bg-[#FCF8F9] text-gray-700 font-bold text-sm hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deletingAccount || deleteConfirmText.trim().toUpperCase() !== 'DELETE'}
              className="flex-1 py-3.5 rounded-full bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {deletingAccount ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // DESKTOP VIEW
  // ============================================================
  const DesktopView = () => (
    <div className="hidden lg:block max-w-4xl mx-auto pb-20">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Profile</h1>
        <p className="text-gray-500 mt-1">Manage your account and preferences</p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-6 mb-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6] shrink-0 overflow-hidden">
            {userData?.profileImage ? (
              <img src={userData.profileImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={40} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#EFA9B6]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#EFA9B6]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-2 text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-serif font-bold text-gray-900">{userData?.name || 'User'}</h2>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Mail size={14} /> {userData?.email}</span>
                  <span className="flex items-center gap-1"><Phone size={14} /> {userData?.phone || 'Not set'}</span>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isEditing && (
              <button
                onClick={handleCancelEdit}
                className="px-6 py-2.5 rounded-full text-sm font-bold transition-colors bg-gray-50 text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
            )}
            <button
              onClick={() => isEditing ? handleSave() : setIsEditing(true)}
              disabled={loading}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-colors flex items-center gap-2 ${
                isEditing
                  ? 'bg-[#010101] text-white hover:bg-black/90'
                  : 'bg-[#FFF5F6] text-[#EFA9B6] hover:bg-[#FBE1E8]'
              }`}
            >
              {isEditing ? (
                loading ? 'Saving...' : <><Save size={16} /> Save</>
              ) : (
                <><Edit2 size={16} /> Edit Profile</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <button
          onClick={() => navigate('/dashboard/calendar')}
          className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 hover:border-[#EFA9B6] transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFF5F6] rounded-full flex items-center justify-center text-[#EFA9B6]">
              <Calendar size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{appointments.length}</p>
              <p className="text-xs text-gray-400">Appointments</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/dashboard/orders')}
          className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 hover:border-[#EFA9B6] transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFF5F6] rounded-full flex items-center justify-center text-[#EFA9B6]">
              <ShoppingBag size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
              <p className="text-xs text-gray-400">Orders</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate('/dashboard/favorites')}
          className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 hover:border-[#EFA9B6] transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFF5F6] rounded-full flex items-center justify-center text-[#EFA9B6]">
              <Heart size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{favorites.length}</p>
              <p className="text-xs text-gray-400">Favorites</p>
            </div>
          </div>
        </button>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFF5F6] rounded-full flex items-center justify-center text-[#EFA9B6]">
              <Clock size={20} />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{getMemberSince()}</p>
              <p className="text-xs text-gray-400">Member Since</p>
            </div>
          </div>
        </div>
      </div>

      {/* Skytone Pro Card */}
      <div className="bg-gradient-to-br from-[#010101] to-gray-900 rounded-[2rem] p-6 mb-8 shadow-xl text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#CDBCE7]/20 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-white/10 p-2 rounded-full backdrop-blur-md">
            <Briefcase size={18} className="text-[#CDBCE7]" />
          </div>
          <h2 className="font-serif text-lg font-bold">Skytone Pro</h2>
        </div>
        <p className="text-xs text-gray-400 mb-5 leading-relaxed pr-4">
          Are you a beauty professional? Onboard your services, manage bookings, and request deposits seamlessly.
        </p>
        <button
          onClick={handleSwitchToPro}
          className="w-full bg-white text-gray-900 py-3.5 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors shadow-lg flex items-center justify-center gap-2"
        >
          Open Business Dashboard <ArrowRight size={16} />
        </button>
      </div>

      {/* Settings Section */}
      <div className="mb-8">
        <h2 className="text-lg font-serif font-bold text-gray-900 mb-4">Settings</h2>
        <SettingsList />
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-3xl shadow-sm border border-gray-50 p-6 mb-6">
        <h3 className="text-lg font-serif font-bold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {appointments.length > 0 ? (
            appointments.slice(0, 3).map((apt, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-700">
                  Booked: {apt.serviceTitle || apt.serviceName || 'Appointment'}
                </span>
                <span className="text-xs text-gray-400">
                  {apt.date ? new Date(apt.date).toLocaleDateString() : 'Upcoming'}
                </span>
              </div>
            ))
          ) : orders.length > 0 ? (
            orders.slice(0, 3).map((order, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <span className="text-sm text-gray-700">
                  Ordered: {order.items?.[0]?.name || 'Products'}
                </span>
                <span className="text-xs text-gray-400">
                  R{Number(order.total || 0).toFixed(2)}
                </span>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 text-sm">No recent activity</p>
            </div>
          )}
        </div>
      </div>

      {/* Sign Out */}
      <button
        onClick={handleLogout}
        disabled={loading}
        className="w-full p-4 bg-red-50 text-red-500 rounded-3xl font-bold text-sm hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
      >
        <LogOut size={18} />
        {loading ? 'Signing out...' : 'Sign Out'}
      </button>
    </div>
  );

  // ============================================================
  // MOBILE VIEW
  // ============================================================
  const MobileView = () => (
    <div className="lg:hidden h-full overflow-y-auto bg-[#FCF8F9] font-sans relative pb-28">
      <div className="absolute top-[-50px] right-[-50px] w-64 h-64 bg-[#EFA9B6]/20 rounded-full blur-3xl opacity-60 pointer-events-none"></div>

      {/* User Card */}
      <div className="px-6 pt-12 pb-6 relative z-10">
        <div className="flex items-center gap-4 bg-white rounded-[2rem] p-4 shadow-sm border border-gray-50">
          <div className="w-14 h-14 bg-[#FFF5F6] rounded-full flex items-center justify-center text-[#EFA9B6] shrink-0 overflow-hidden">
            {userData?.profileImage ? (
              <img src={userData.profileImage} alt="" className="w-full h-full object-cover" />
            ) : (
              <User size={28} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-serif font-bold text-gray-900">{userData?.name || 'User'}</h2>
            <p className="text-xs text-gray-500 truncate">{userData?.email}</p>
          </div>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-[#FFF5F6] text-[#EFA9B6] text-xs font-bold rounded-full shrink-0 active:bg-[#FBE1E8]"
            style={{ touchAction: 'manipulation' }}
          >
            Edit
          </button>
        </div>

        {isEditing && (
          <div className="mt-4 bg-white rounded-2xl p-4 space-y-3 border border-gray-50">
            <div>
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#EFA9B6]"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-[#EFA9B6]"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex-1 py-3 rounded-full bg-gray-50 text-gray-600 font-bold text-sm active:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={loading}
                className="flex-1 py-3 rounded-full bg-[#010101] text-white font-bold text-sm active:bg-black/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} /> Save
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick links — mobile */}
      <div className="px-6 pb-6 relative z-10">
        <div className="bg-white rounded-3xl border border-gray-50 overflow-hidden">
          <button
            onClick={() => navigate('/dashboard/orders')}
            className="w-full p-4 flex items-center gap-4 active:bg-[#FCF8F9] transition-colors group text-left border-b border-gray-50"
          >
            <div className="w-10 h-10 rounded-full bg-[#FCF8F9] text-gray-500 flex items-center justify-center group-hover:text-[#EFA9B6] group-hover:bg-[#FFF5F6] transition-colors">
              <Package size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900">My Orders</p>
              <p className="text-[11px] text-gray-400 truncate">
                {orders.length > 0
                  ? `${orders.length} order${orders.length === 1 ? '' : 's'}`
                  : 'No orders yet'}
              </p>
            </div>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-[#EFA9B6] transition-colors shrink-0" />
          </button>

          <button
            onClick={() => setShowAddressModal(true)}
            className="w-full p-4 flex items-center gap-4 active:bg-[#FCF8F9] transition-colors group text-left border-b border-gray-50"
          >
            <div className="w-10 h-10 rounded-full bg-[#FCF8F9] text-gray-500 flex items-center justify-center group-hover:text-[#EFA9B6] group-hover:bg-[#FFF5F6] transition-colors">
              <MapPin size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900">Shipping Address</p>
              <p className="text-[11px] text-gray-400 truncate">{getAddressPreview()}</p>
            </div>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-[#EFA9B6] transition-colors shrink-0" />
          </button>

          <button
            onClick={() => navigate('/dashboard/favorites')}
            className="w-full p-4 flex items-center gap-4 active:bg-[#FCF8F9] transition-colors group text-left"
          >
            <div className="w-10 h-10 rounded-full bg-[#FCF8F9] text-gray-500 flex items-center justify-center group-hover:text-[#EFA9B6] group-hover:bg-[#FFF5F6] transition-colors">
              <Heart size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm text-gray-900">Favorites</p>
              <p className="text-[11px] text-gray-400 truncate">
                {favorites.length > 0
                  ? `${favorites.length} saved item${favorites.length === 1 ? '' : 's'}`
                  : 'No favorites yet'}
              </p>
            </div>
            <ChevronRight size={16} className="text-gray-300 group-hover:text-[#EFA9B6] transition-colors shrink-0" />
          </button>
        </div>
      </div>

      {/* Member since — mobile */}
      <div className="px-6 pb-6 relative z-10">
        <div className="bg-white rounded-3xl p-4 border border-gray-50 flex items-center gap-3">
          <div className="w-10 h-10 bg-[#FFF5F6] rounded-full flex items-center justify-center text-[#EFA9B6]">
            <Clock size={18} />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900">{getMemberSince()}</p>
            <p className="text-[11px] text-gray-400">Member Since</p>
          </div>
        </div>
      </div>

      {/* Skytone Pro Card */}
      <div className="px-6 pb-6 relative z-10">
        <div className="bg-gradient-to-br from-[#010101] to-gray-900 rounded-[2rem] p-6 shadow-xl text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#CDBCE7]/20 rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
          <div className="flex items-center gap-3 mb-3">
            <div className="bg-white/10 p-2 rounded-full backdrop-blur-md">
              <Briefcase size={18} className="text-[#CDBCE7]" />
            </div>
            <h2 className="font-serif text-lg font-bold">Skytone Pro</h2>
          </div>
          <p className="text-xs text-gray-400 mb-5 leading-relaxed pr-4">
            Are you a beauty professional? Onboard your services, manage bookings, and request deposits seamlessly.
          </p>
          <button
            onClick={handleSwitchToPro}
            className="w-full bg-white text-gray-900 py-3.5 rounded-full font-bold text-sm hover:bg-gray-100 transition-colors shadow-lg flex items-center justify-center gap-2"
          >
            Open Business Dashboard <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Upcoming Appointments */}
      <div className="px-6 pb-6 relative z-10">
        <div className="flex justify-between items-end mb-4">
          <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase">Upcoming Appointments</h2>
          {appointments.length > 0 && (
            <button
              onClick={() => navigate('/dashboard/calendar')}
              className="text-[11px] text-[#EFA9B6] font-bold"
            >
              View all
            </button>
          )}
        </div>
        {appointments.length > 0 ? (
          appointments.slice(0, 1).map((apt, index) => (
            <button
              key={index}
              onClick={() => navigate('/dashboard/calendar')}
              className="w-full text-left bg-white rounded-[2rem] p-4 shadow-sm border border-gray-50 flex gap-4 items-center hover:border-[#EFA9B6] transition-colors"
            >
              <div className="bg-gradient-to-b from-[#FFF5F6] to-[#FBE1E8] w-[4.5rem] h-[4.5rem] rounded-2xl flex flex-col items-center justify-center text-[#EFA9B6] shadow-sm border border-white shrink-0">
                <span className="text-[11px] font-bold uppercase tracking-wider">
                  {apt.date ? new Date(apt.date).toLocaleString('default', { month: 'short' }) : 'TBD'}
                </span>
                <span className="text-2xl font-serif font-bold leading-none mt-0.5 text-gray-900">
                  {apt.date ? new Date(apt.date).getDate() : '--'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-900 text-[15px] mb-0.5 truncate">
                  {apt.serviceTitle || apt.serviceName || 'Appointment'}
                </h3>
                <p className="text-xs text-gray-500 font-medium truncate">
                  {apt.professionalName || 'Provider'} • {apt.time || 'TBD'}
                </p>
              </div>
              <div className="w-10 h-10 bg-[#FCF8F9] rounded-full flex items-center justify-center text-[#EFA9B6] shrink-0">
                <Calendar size={18} />
              </div>
            </button>
          ))
        ) : (
          <div className="text-center py-8 bg-white rounded-2xl border border-gray-50">
            <p className="text-gray-400 text-sm">No upcoming appointments</p>
          </div>
        )}
      </div>

      {/* Recent Orders */}
      <div className="px-6 pb-6 relative z-10">
        <div className="flex justify-between items-end mb-4">
          <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase">Recent Orders</h2>
          {orders.length > 0 && (
            <button
              onClick={() => navigate('/dashboard/orders')}
              className="text-[11px] text-[#EFA9B6] font-bold"
            >
              View all
            </button>
          )}
        </div>
        {orders.length > 0 ? (
          orders.slice(0, 1).map((order, index) => (
            <button
              key={index}
              onClick={() => navigate('/dashboard/orders')}
              className="w-full text-left bg-white rounded-3xl p-6 shadow-sm border border-gray-50 hover:border-[#EFA9B6] transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="bg-[#FFF5F6] p-2.5 rounded-xl">
                  <ShoppingBag size={18} className="text-[#EFA9B6]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[14px] text-gray-900 truncate">
                    {order.items?.[0]?.name || 'Order'}
                    {order.items?.length > 1 && ` +${order.items.length - 1}`}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-0.5">
                    #{order.id?.slice(-8).toUpperCase() || 'ORD-001'}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                <span className="text-xs text-gray-500 capitalize">
                  {order.fulfillmentStatus?.replace(/_/g, ' ') || order.status || 'Processing'}
                </span>
                <span className="font-bold text-gray-900">
                  R{Number(order.total || 0).toFixed(2)}
                </span>
              </div>
            </button>
          ))
        ) : (
          <div className="text-center py-8 bg-white rounded-2xl border border-gray-50">
            <p className="text-gray-400 text-sm">No orders yet</p>
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="px-6 pb-6 relative z-10">
        <h2 className="text-sm font-bold text-gray-900 tracking-wide uppercase mb-4">Settings</h2>
        <SettingsList compact />
      </div>

      {/* Sign Out */}
      <div className="px-6 pb-6 relative z-10">
        <button
          onClick={handleLogout}
          disabled={loading}
          className="w-full p-4 bg-red-50 text-red-500 rounded-3xl font-bold text-sm hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
        >
          <LogOut size={18} />
          {loading ? 'Signing out...' : 'Sign Out'}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <DesktopView />
      <MobileView />
      {showAddressModal && <AddressModal />}
      {showDeleteModal && <DeleteAccountModal />}

      {addressToast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[70]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            addressToast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {addressToast.msg}
          </div>
        </div>
      )}
    </>
  );
};

const Field = ({ label, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1.5">
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6] transition-colors"
    />
  </div>
);

export default ProfilePage;